import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { workItemAttachmentsApi } from '@/api/workItemAttachments'
import type { PaginationParams } from '@/types'

const attachmentsKey = (projectId: string, workItemId: string, params?: Record<string, unknown>) =>
  params
    ? ['projects', projectId, 'work-items', workItemId, 'attachments', params] as const
    : ['projects', projectId, 'work-items', workItemId, 'attachments'] as const

export function useWorkItemAttachments(projectId: string, workItemId: string, pagination?: PaginationParams) {
  const params = pagination ? { ...pagination } : undefined

  return useQuery({
    queryKey: attachmentsKey(projectId, workItemId, params),
    queryFn: () => workItemAttachmentsApi.getByWorkItem(projectId, workItemId, params),
    enabled: !!projectId && !!workItemId,
    staleTime: 0,
  })
}

export function useUploadWorkItemAttachments(projectId: string, workItemId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (files: File[]) => workItemAttachmentsApi.upload(projectId, workItemId, files),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: attachmentsKey(projectId, workItemId),
      })
    },
  })
}

export function useDeleteWorkItemAttachment(projectId: string, workItemId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (attachmentId: string) =>
      workItemAttachmentsApi.delete(projectId, workItemId, attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: attachmentsKey(projectId, workItemId),
      })
    },
  })
}

export function useWorkItemAttachmentDownloadUrl(projectId: string, workItemId: string) {
  return useMutation({
    mutationFn: (attachmentId: string) =>
      workItemAttachmentsApi.getDownloadUrl(projectId, workItemId, attachmentId),
    onSuccess: (url) => {
      window.open(url, '_blank', 'noopener,noreferrer')
    },
  })
}
